"use client"

import { Button } from "@/components/ui/button"
import { useState } from "react"
import { Input } from "@/components/ui/input"

/**
 * Add a comment to a post, whether directly or in response to another comment.
 *
 * Also allows editing an existing comment if editExistingCommentId is provided.
 *
 * @param placeholder - The placeholder text for the input.
 * @param ctaText - The text for the button to submit the comment.
 * @param editExistingCommentText - The text of the comment to edit. Optional.
 * @param onSubmit - A callback function to call to submit the comment creation or update.
 * @param onSuccess - A callback function to call when the comment is successfully added or updated.
 *
 * @returns A form with an input for the comment text and a button to submit the comment.
 */
export default function AddOrEditCommentInput({
  placeholder,
  ctaText,
  editExistingCommentText,
  onSubmitStart,
  onSubmit,
  onSuccess,
}: {
  placeholder: string
  ctaText: string
  editExistingCommentText?: string
  onSubmitStart?: () => void
  onSubmit: (text: string) => Promise<void>
  onSuccess?: () => void
}) {
  const [commentText, setCommentText] = useState(editExistingCommentText || "")
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    setIsLoading(true)
    onSubmitStart?.()

    // Call the onSubmit callback to submit the comment creation or update.
    await onSubmit(commentText)

    setCommentText("")
    setIsLoading(false)

    // Call the onSuccess callback if provided.
    onSuccess?.()
  }

  return (
    <form className="flex flex-row items-center w-full" onSubmit={handleSubmit}>
      <Input
        placeholder={placeholder}
        className="ml-3 rounded-sm"
        value={commentText}
        onChange={(e) => setCommentText(e.target.value)}
      />
      <Button
        variant={commentText.length > 0 ? "default" : "secondary"}
        disabled={commentText.length === 0 || isLoading}
        className="ml-3 w-20"
        onClick={handleSubmit}
      >
        {isLoading ? (
          <div className="flex items-center">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </div>
        ) : (
          ctaText
        )}
      </Button>
    </form>
  )
}
